import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { Chip, Typography } from '@mui/material';
import EsgList from '../../components/esgmng/EsgList';
import EsgListBox from '../../components/esgmng/EsgListBox';
import { listEsgBoards, unloadEsgBoards } from '../../modules/esgModule/esgBoards';

const CATEGORY_COLORS = { E: 'success', S: 'info', G: 'warning' };
const CATEGORY_LABELS = { E: 'E-환경', S: 'S-사회', G: 'G-지배구조' };

const EsgListContainer = ({ searchData, onSearch, selectedBoard, handleAdd }) => {
  const dispatch = useDispatch();

  const { esgBoards, loading } = useSelector(({ esgModule, loading }) => ({
    esgBoards: esgModule.esgBoards.esgBoards,
    loading: loading['esg/LIST_ESG_BOARDS'],
  }));

  useEffect(() => {
    dispatch(listEsgBoards(searchData));
    return () => {
      dispatch(unloadEsgBoards());
    };
  }, [searchData]);

  const formik = useFormik({
    initialValues: { searchKeyword: 'wrTitle', searchWords: '' },
    validationSchema: yup.object({
      searchKeyword: yup.string().nullable(),
      searchWords: yup.string().trim().nullable(),
    }),
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);
      const search = { [values.searchKeyword]: values.searchWords };
      setTimeout(() => {
        onSearch(search);
        setSubmitting(false);
      }, 500);
    },
  });

  const columns = useMemo(
    () => [
      { field: 'id', headerName: 'ID', hide: true, type: 'string' },
      { field: 'wrUuid', hide: true, type: 'string' },
      {
        field: 'wrCategory',
        headerName: '카테고리',
        width: 120,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) =>
          params.value ? (
            <Chip
              label={CATEGORY_LABELS[params.value] || params.value}
              color={CATEGORY_COLORS[params.value] || 'default'}
              size="small"
            />
          ) : null,
      },
      {
        field: 'wrTitle',
        headerName: '제목',
        minWidth: 200,
        flex: 0.8,
        renderCell: (params) => (
          <Typography variant="subtitle2" fontWeight="bold">
            {params.value}
          </Typography>
        ),
      },
      {
        field: 'wrContent',
        headerName: '내용',
        minWidth: 300,
        flex: 1,
        sortable: false,
        renderCell: (params) => {
          const plainText = params.value
            ? params.value.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim()
            : '';
          return (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                whiteSpace: 'pre-wrap',
                lineHeight: 1.5,
              }}
            >
              {plainText}
            </Typography>
          );
        },
      },
      {
        field: 'wrFileCnt',
        headerName: '첨부',
        width: 60,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) =>
          params.value && params.value !== '0' ? (
            <Chip label={params.value} size="small" color="primary" variant="outlined" />
          ) : null,
      },
      { field: 'crtNm', headerName: '작성자', width: 90 },
      { field: 'crtDt', headerName: '작성일', width: 160 },
    ],
    [],
  );

  const handleRowClick = (e, params) => {
    if (!e.ignore) {
      selectedBoard(params.row);
    }
  };

  return (
    <div>
      <EsgListBox formik={formik} handleAdd={handleAdd} />
      <EsgList
        columns={columns}
        rows={esgBoards}
        loading={loading}
        handleRowClick={handleRowClick}
        columnVisibilityModel={{ id: false, wrUuid: false }}
      />
    </div>
  );
};

export default EsgListContainer;
